<template>
    <div
        class="upload-container h-full relative border-2 border-dashed border-gray-300 rounded-lg text-center my-2 p-4 bg-cover bg-center bg-no-repeat flex items-center justify-center"
        :style="{
            backgroundImage: `url(${previewImage || selectedImage})`,
            minHeight: `${height}px`,
        }"
        :class="{ 'drag-over': isDragOver }"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @dragend.prevent="onDragLeave"
        @drop.prevent="onDrop"
    >
        <!-- Loading overlay -->
        <div
            v-if="regenerateImage"
            class="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75 rounded-lg z-30"
        >
            <div class="flex flex-col items-center">
                <svg
                    class="animate-spin h-8 w-8 text-gray-500 mb-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    ></circle>
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                </svg>
                <span class="text-sm text-gray-600">
                    Generating new image...
                </span>
            </div>
        </div>
        <div
            v-if="!regenerateImage"
            class="drag-info d-none absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-gray-400"
        >
            <icon icon="no-image" :height="16" :width="16" />
            <br />
            <strong>Drop Cover Here</strong>
        </div>
        <div
            class="bg-slate-100/50 backdrop-blur text-slate-800 rounded p-6 w-full max-w-3xl"
        >
            <div v-if="!regenerateImage" class="upload-text">
                <icon icon="no-image" :height="16" :width="16" />
                <strong>Upload or drag & drop an image here</strong>
                <p>We Support PNGs, JPEGs, WEBPs under 10MB</p>
            </div>
            <div v-if="!regenerateImage" class="upload-buttons">
                <button
                    class="btn btn-secondary flex items-center p-3 lg:px-2.5 lg:py-2 rounded whitespace-nowrap bg-white"
                    @click="triggerFileUpload"
                >
                    <icon icon="upload" :height="16" :width="16" />
                    <span class="ml-2 text-sm font-medium">Upload</span>
                </button>

                <button
                    class="btn btn-secondary flex items-center p-3 lg:px-2.5 lg:py-2 rounded whitespace-nowrap bg-white"
                    @click="fetchUnsplashImages"
                    :style="{
                        cursor: isDestinationSet ? 'pointer' : 'not-allowed',
                    }"
                >
                    <icon icon="rotate" :height="16" :width="16" />
                    <span class="ml-2 text-sm font-medium">Generate</span>
                </button>
            </div>

            <input
                type="file"
                @change="handleFileUpload"
                accept="image/jpeg, image/png, image/webp"
                class="hidden-input"
                ref="fileInput"
            />
        </div>
        <div class="image-actions z-50">
            <a
                class="icon-button"
                href="#"
                @click="handleSaveImage()"
                @mouseover="showTooltip('save')"
                @mouseout="hideTooltip('save')"
                v-if="imageChanged"
            >
                <icon icon="check" :height="16" :width="16" />
                <div
                    v-if="tooltipVisible.save"
                    class="absolute top-[-35px] left-[10%] transform -translate-x-1/2 z-[1000] block opacity-100 bg-[#213e44] text-white p-[5px] rounded text-xs whitespace-nowrap"
                >
                    Save Image
                </div>
            </a>
            <a
                v-if="selectedImage"
                class="icon-button"
                href="#"
                @click="triggerFileUpload"
                @mouseover="showTooltip('upload')"
                @mouseout="hideTooltip('upload')"
            >
                <icon icon="upload" :height="16" :width="16" />
                <div
                    v-if="tooltipVisible.upload"
                    class="absolute top-[-35px] left-[36%] transform -translate-x-1/2 z-[1000] block opacity-100 bg-[#213e44] text-white p-[5px] rounded text-xs whitespace-nowrap"
                >
                    Upload
                </div>
            </a>

            <a
                v-if="selectedImage || previewImage"
                class="icon-button"
                href="#"
                @click="fetchUnsplashImages"
                @mouseover="showTooltip('regenerate')"
                @mouseout="hideTooltip('regenerate')"
            >
                <icon icon="rotate" :height="16" :width="16" />
                <div
                    v-if="tooltipVisible.regenerate"
                    class="absolute top-[-35px] left-[64%] transform -translate-x-1/2 z-[1000] block opacity-100 bg-[#213e44] text-white p-[5px] rounded text-xs whitespace-nowrap"
                >
                    Regenerate
                </div>
            </a>
            <input
                type="file"
                @change="handleFileUpload"
                accept="image/jpeg, image/png, image/webp"
                class="hidden-input"
                ref="fileInput"
            />
            <a
                v-if="selectedImage"
                class="icon-button"
                href="#"
                @click="clearImage"
                @mouseover="showTooltip('delete')"
                @mouseout="hideTooltip('delete')"
            >
                <icon icon="trash" :height="16" :width="16" />
                <div
                    v-if="tooltipVisible.delete"
                    class="absolute top-[-35px] left-[89%] transform -translate-x-1/2 z-[1000] block opacity-100 bg-[#213e44] text-white p-[5px] rounded text-xs whitespace-nowrap"
                >
                    Delete
                </div>
            </a>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { notify } from 'notiwind';
import { useTripsStore } from '@/store/trips';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    trip: {
        type: Object,
        default: () => {
            id: null;
        },
    },
    query: {
        type: String,
        default: '',
    },
    image: {
        type: String,
        default: '',
    },
    height: {
        type: Number,
        default: 200,
    },
    showConfirmation: {
        type: Boolean,
        default: false,
    },
});
const paddingTop = computed(() => {
    if (props.height <= 200) return 25;
    return (props.height - 200) / 2;
});
const selectedImage = ref(props.image);
const previewImage = ref(null);
const emit = defineEmits(['image-selected', 'save']);
const imageLoaded = ref(false);
const imageChanged = ref(false);
const fileInput = ref(null);
const isDragOver = ref(false);
const imageSource = ref(null); // 'upload' or 'generate'
const lastUnsplashFetchTime = ref(null);
const unsplashMinutesRateLimit = 5;
const unsplashRateLimit = 15;
const currentUnsplashFetchCount = ref(0);
const uploadImageMaxMbSize = 5;
const regenerateImage = ref(false);

const isDestinationSet = computed(() => {
    return !!props.query;
});

const onDragOver = () => {
    isDragOver.value = true;
};

const onDragLeave = () => {
    isDragOver.value = false;
};
const onDrop = (event) => {
    isDragOver.value = false;
    const file = event.dataTransfer.files[0];
    handleFile(file);
};

const handleFileUpload = (event) => {
    const file = event.target.files[0];
    handleFile(file);
};

const handleFile = (file) => {
    const maxSize = uploadImageMaxMbSize * 1024 * 1024;
    if (
        file &&
        (file.type === 'image/jpeg' ||
            file.type === 'image/png' ||
            file.type === 'image/webp') &&
        file.size <= maxSize
    ) {
        console.log('VALID handleFile', file);
        const reader = new FileReader();
        reader.onload = (e) => {
            console.log(e);
            previewImage.value = e.target.result;
            imageLoaded.value = true;
            imageSource.value = 'upload';
            emit('image-selected', e.target.result);
            imageChanged.value = true;
        };
        reader.readAsDataURL(file);
    } else {
        notify({
            group: 'error',
            title: 'Invalid Image',
            text: `Please upload a JPEG, PNG or WEBP image less than ${uploadImageMaxMbSize}MB`,
        });
    }
};

const handleSaveImage = () => {
    selectedImage.value = previewImage.value;
    console.log('handleSaveImage', selectedImage.value);
    emit('save', selectedImage.value);
    setTimeout(() => {
        imageChanged.value = false;
    }, 250);
};

const fetchUnsplashImages = async () => {
    if (!isDestinationSet.value) {
        useTripsStore().tripFormErrors.country = [
            'Please select a destination to generate image',
        ];
        return;
    }
    if (
        lastUnsplashFetchTime.value &&
        new Date() - lastUnsplashFetchTime.value <
            unsplashMinutesRateLimit * 60 * 1000
    ) {
        if (currentUnsplashFetchCount.value >= unsplashRateLimit) {
            notify({
                group: 'error',
                title: 'Rate Limit Exceeded',
                text: 'Wait a few minutes and try again later',
            });
            return;
        } else {
            currentUnsplashFetchCount.value++;
        }
    } else {
        lastUnsplashFetchTime.value = new Date();
        currentUnsplashFetchCount.value = 1;
    }

    if (!isDestinationSet.value) return;
    imageLoaded.value = false;
    regenerateImage.value = true;
    try {
        const response = await ApiService.apiCall(
            `/media/random-image/${props.query}`,
            'get'
        );
        const newImage = response.data.images.regular;
        const img = new Image();

        img.onload = () => {
            regenerateImage.value = false;
            previewImage.value = newImage;
            imageSource.value = 'generate';
            emit('image-selected', previewImage.value);
            imageChanged.value = true;
            imageLoaded.value = true;
            if (!props.trip.id) {
                console.log('handleNewImage');
                handleSaveImage();
            }
        };
        img.src = newImage;
    } catch (error) {
        console.error(error);
        notify({
            group: 'error',
            title: 'Error',
            text: 'Failed to fetch image',
        });
        imageLoaded.value = true;
        regenerateImage.value = false;
    }
};

const triggerFileUpload = () => {
    fileInput.value.value = '';
    fileInput.value.click();
};

const clearImage = () => {
    selectedImage.value = null;
    imageLoaded.value = false;
    imageSource.value = null;
    emit('image-selected', null);
};

watch(
    () => props.image,
    (newValue) => {
        if (newValue) {
            if (newValue && !imageChanged.value) {
                const img = new Image();
                img.onload = () => {
                    selectedImage.value = newValue;
                    imageLoaded.value = true;
                };
                img.src = newValue;
            }
        }
    },
    { immediate: true }
);

const tooltipVisible = ref({
    upload: false,
    regenerate: false,
    delete: false,
    save: false,
});

const showTooltip = (tooltip) => {
    tooltipVisible.value = { ...tooltipVisible.value, [tooltip]: true };
};

const hideTooltip = (tooltip) => {
    tooltipVisible.value = { ...tooltipVisible.value, [tooltip]: false };
};
</script>

<style scoped>
.upload-container {
    & .drag-info {
        display: none;
    }

    &.drag-over {
        background-color: #f0f0f0;
        background-image: none !important;

        & .upload-buttons,
        & .upload-text {
            display: none;
        }

        & .drag-info {
            display: block;
        }
    }
}

.upload-text strong {
    display: block;
    font-size: 18px;
    margin-bottom: 5px;
}

.upload-text p {
    margin-bottom: 20px;
}

.upload-buttons {
    display: flex;
    justify-content: center;
    gap: 10px;
}

.hidden-input {
    display: none;
}

.image-actions {
    position: absolute;
    bottom: 10px;
    right: 10px;
    display: flex;
    gap: 10px;
}

.icon-button {
    background-color: #fff;
    border-radius: 8px;
    padding: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
}

.icon-button i {
    color: #000;
    margin: auto;
}
</style>
